import { AccountType } from "@common/constants/domains/accounting/accounts";
import { AllocateCodeText, DraftAccount, DraftState } from "./draftState";
import {
    applyDraftSortOrders,
    DraftPlacedRow,
    orderedScopeIdsInScope,
    orderedScopeMembers,
    planDraftScopePlacement,
} from "./draftSortOrder";
import { MoveInsertPosition, MoveRowKind } from "./draftMoveOperations";
import { clearIneligibleForm1099Box } from "./draftForm1099Box";
import { retypeGroupSubtrees } from "./draftSubtreeRetype";

// A single member of a multi-row drag: the row id plus whether it's an account
// or a custom group. The caller passes these already in tree order and already
// de-duplicated (descendants of a selected group removed), since `moveRows`
// assumes a pre-validated, top-level-only set.
export interface MoveRowsMember {
    id: string;
    kind: MoveRowKind;
}

// A validated multi-row drop: the dragged members plus their destination scope,
// the sibling they should sit beside (`overRefId`, null = append at the end of
// the destination scope) and whether they land before/after it. `overRefId` may
// be an account or a group id — the destination is a single combined ordering
// space.
export interface MoveRowsArgs {
    rows: MoveRowsMember[];
    // A multi-row drag across the merge view is run once per entity, each call
    // over only that entity's constituents.
    entityId: number;
    targetType: AccountType;
    targetParentGroupId: string | null;
    overRefId: string | null;
    position: MoveInsertPosition;
}

// Report-leaf folding is presentation-only; exact type changes regenerate codes.
const isTypeChange = (from: AccountType, to: AccountType): boolean =>
    from !== to;

// Splices a contiguous block of ids into an ordered scope at the slot implied
// by `overRefId` + `position`. When the reference isn't in the scope (e.g. the
// drop landed beside a moved row) the block is appended.
const spliceBlock = ({
    order,
    block,
    overRefId,
    position,
}: {
    order: string[];
    block: string[];
    overRefId: string | null;
    position: MoveInsertPosition;
}): string[] => {
    const refIndex = overRefId ? order.indexOf(overRefId) : -1;
    let insertIndex: number;
    if (refIndex === -1) {
        insertIndex = order.length;
    } else {
        insertIndex = position === "after" ? refIndex + 1 : refIndex;
    }
    return [
        ...order.slice(0, insertIndex),
        ...block,
        ...order.slice(insertIndex),
    ];
};

// Sequential allocated codeTexts for moved custom accounts whose leaf changes.
// Each freshly-assigned value is fed back into the scanned accounts so the
// injected allocator sees it and two relocated accounts can't be handed the same
// code.
const resolveMovedAccountCodes = ({
    accounts,
    movedAccountIds,
    accountsById,
    targetType,
    allocateCodeText,
}: {
    accounts: DraftAccount[];
    movedAccountIds: string[];
    accountsById: Map<string, DraftAccount>;
    targetType: AccountType;
    allocateCodeText: AllocateCodeText;
}): Map<string, string | null> => {
    const codeById = new Map<string, string | null>();
    let scanAccounts = accounts;
    movedAccountIds.forEach((id) => {
        const account = accountsById.get(id);
        if (!account || !isTypeChange(account.type, targetType)) {
            return;
        }
        const codeText = allocateCodeText({
            kind: "relocate",
            type: targetType,
            accounts: scanAccounts,
            currentCodeText: account.codeText,
        });
        codeById.set(id, codeText);
        scanAccounts = scanAccounts.map((row) =>
            row.id === id ? { ...row, codeText } : row,
        );
    });
    return codeById;
};

// Applies a validated multi-row drop to the draft as a single immutable step:
// moves the whole pre-validated, top-level set (accounts and/or groups, in the
// given relative order) into the target scope as a contiguous block. Because the
// destination is ONE combined ordering space, a mixed account+group block lands
// contiguously at the drop slot. Custom accounts changing leaf get a fresh,
// non-colliding code each. Every source scope the block left keeps its
// sortOrders. A selected group carries its whole subtree untouched (its
// descendants must already be excluded from `rows`).
export const moveRows = ({
    state,
    allocateCodeText,
    rows,
    entityId,
    targetType,
    targetParentGroupId,
    overRefId,
    position,
}: {
    state: DraftState;
    allocateCodeText: AllocateCodeText;
} & MoveRowsArgs): DraftState => {
    if (rows.length === 0) {
        return state;
    }
    const movedIds = rows.map((row) => row.id);
    const movedSet = new Set(movedIds);
    const accountsById = new Map(state.accounts.map((row) => [row.id, row]));

    const destExisting = orderedScopeIdsInScope({
        accounts: state.accounts,
        groups: state.groups,
        entityId,
        type: targetType,
        parentGroupId: targetParentGroupId,
    }).filter((id) => !movedSet.has(id));
    const destOrder = spliceBlock({
        order: destExisting,
        block: movedIds,
        overRefId,
        position,
    });

    const nameById = new Map<string, string>([
        ...state.accounts.map((row): [string, string] => [row.id, row.name]),
        ...state.groups.map((row): [string, string] => [row.id, row.name]),
    ]);
    const placedRows: DraftPlacedRow[] = rows.map((row) => ({
        id: row.id,
        kind: row.kind,
        name: nameById.get(row.id) ?? "",
    }));
    const assignments = planDraftScopePlacement({
        members: orderedScopeMembers({
            accounts: state.accounts,
            groups: state.groups,
            entityId,
            type: targetType,
            parentGroupId: targetParentGroupId,
        }),
        placed: placedRows,
        // destOrder includes the block; this is remaining siblings ahead of it.
        insertIndex: destOrder.findIndex((id) => movedSet.has(id)),
    });

    const movedAccountIds = rows
        .filter((row) => row.kind === "account")
        .map((row) => row.id);
    const codeById = resolveMovedAccountCodes({
        accounts: state.accounts,
        movedAccountIds,
        accountsById,
        targetType,
        allocateCodeText,
    });

    const groupsById = new Map(state.groups.map((row) => [row.id, row]));
    const retypedRootGroupIds = rows
        .filter(
            (row) =>
                row.kind === "group" &&
                groupsById.get(row.id)?.type !== undefined &&
                groupsById.get(row.id)?.type !== targetType,
        )
        .map((row) => row.id);
    // Include codes assigned to moved accounts so subtree allocation stays unique.
    const scanAccountsAfterMovedCodes = state.accounts.map((row) =>
        codeById.has(row.id)
            ? { ...row, codeText: codeById.get(row.id) ?? null }
            : row,
    );
    const { descendantGroupIds, codeByAccountId: subtreeCodeById } =
        retypedRootGroupIds.length > 0
            ? retypeGroupSubtrees({
                  state,
                  rootGroupIds: retypedRootGroupIds,
                  targetType,
                  allocateCodeText,
                  initialScanAccounts: scanAccountsAfterMovedCodes,
              })
            : {
                  descendantGroupIds: new Set<string>(),
                  codeByAccountId: new Map<string, string | null>(),
              };

    const accounts = state.accounts.map((row) => {
        if (movedSet.has(row.id)) {
            const moved = {
                ...row,
                type: targetType,
                parentGroupId: targetParentGroupId,
                codeText: codeById.has(row.id)
                    ? (codeById.get(row.id) ?? null)
                    : row.codeText,
            };
            return isTypeChange(row.type, targetType)
                ? clearIneligibleForm1099Box(moved)
                : moved;
        }
        if (subtreeCodeById.has(row.id)) {
            return clearIneligibleForm1099Box({
                ...row,
                type: targetType,
                codeText: subtreeCodeById.get(row.id) ?? null,
            });
        }
        return row;
    });
    const groups = state.groups.map((row) => {
        if (movedSet.has(row.id)) {
            return {
                ...row,
                type: targetType,
                parentGroupId: targetParentGroupId,
            };
        }
        if (descendantGroupIds.has(row.id)) {
            return { ...row, type: targetType };
        }
        return row;
    });
    return {
        ...state,
        accounts: applyDraftSortOrders(accounts, assignments),
        groups: applyDraftSortOrders(groups, assignments),
    };
};
